import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Flame, Loader2, Trash2, X } from "lucide-react";
import { Btn, Section } from "@/components/account/Shell";
import { Cell, Filters, IconBtn, PillRow, Sheet, Tag, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { ServiceRequests } from "@/components/admin/ServiceRequests";
import { when } from "@/lib/when";

const money = (n) => (n === null || n === undefined ? "Offers" : `$${Number(n).toLocaleString()}`);
const FILTERS = [
    { key: "pending", label: "In review" },
    { key: "active", label: "Live" },
    { key: "sold", label: "Sold" },
    { key: "", label: "All" },
];
const AD_COLS = [
    { key: "photo", label: "Photo", w: "64px" },
    { key: "when", label: "Posted", sub: "date · how long ago", w: "150px" },
    { key: "title", label: "Ad", sub: "title · kind", w: "minmax(240px,2fr)" },
    { key: "price", label: "Price", w: "100px" },
    { key: "seller", label: "Seller", w: "minmax(160px,1fr)" },
    { key: "status", label: "Status", sub: "in review · live · sold · featured", w: "150px" },
    { key: "actions", label: "Actions", sub: "approve · reject · feature · remove", w: "260px" },
];
const MSG_COLS = [
    { key: "when", label: "Received", sub: "date · how long ago", w: "150px" },
    { key: "who", label: "From", sub: "name · contact", w: "minmax(200px,1.2fr)" },
    { key: "ad", label: "About", sub: "the ad they asked on", w: "minmax(180px,1fr)" },
    { key: "message", label: "Message", w: "minmax(300px,2.4fr)" },
];

export default function MarketplaceAdmin() {
    const [filter, setFilter] = useState("pending");
    const [data, setData] = useState(null);
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        setLoading(true);
        Promise.all([
            api.get("/admin/marketplace/listings", { params: filter ? { status: filter } : {} }),
            api.get("/admin/marketplace/inquiries"),
        ]).then(([a, b]) => { setData(a.data); setInquiries(b.data.inquiries); })
            .catch(() => { }).finally(() => setLoading(false));
    };
    useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const act = async (id, decision) => {
        try { await api.post(`/admin/marketplace/listings/${id}/review?decision=${decision}`); toast.success(`Listing ${decision}d`); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const feature = async (l) => {
        try {
            await api.post(`/admin/marketplace/listings/${l.id}/feature?days=${l.featured ? 0 : 7}`);
            toast.success(l.featured ? "Feature removed" : "Featured for 7 days");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    if (loading && !data) return <div className="flex min-h-[30vh] items-center justify-center text-[#4a4a4a]"><Loader2 className="animate-spin" /></div>;
    const listings = data?.listings || [];

    return (
        <div data-testid="admin-marketplace">
            <Filters testid="admin-marketplace-filters">
                <PillRow label="Show" testid="mp-filter" value={filter} onPick={setFilter}
                    choices={FILTERS.map((f) => ({ ...f, count: f.key === "pending" ? data?.pending : undefined }))} />
                {data?.pending > 0 && (
                    <p className="text-[12px] font-semibold text-[#b54708]" data-testid="mp-pending-count">{data.pending} awaiting review</p>
                )}
            </Filters>

            <div className="mt-3 space-y-3">
                <Section title={`${listings.length} classified ads`} pad={false} testid="mp-listings-section" note="every member ad waits here for a look before it goes live">
                    <Sheet cols={AD_COLS} rows={listings} rowKey={(l) => l.id} testid="mp-sheet" minWidth={1100}
                        rowTestid={(l) => `mp-listing-${l.slug}`} empty="Nothing here."
                        cells={(l) => ({
                            when: <Cell><Txt sub>{when(l.created_at)}</Txt></Cell>,
                            photo: (
                                <Cell>
                                    <span className="h-8 w-10 shrink-0 overflow-hidden border border-[#d0d0d0] bg-[#fafafa]">
                                        {l.images?.[0] && <img src={mediaUrl(l.images[0])} alt="" className="h-full w-full object-cover" />}
                                    </span>
                                </Cell>
                            ),
                            title: <Cell><Two a={l.title} b={l.kind} /></Cell>,
                            price: <Cell><Txt bold mono>{money(l.price)}</Txt></Cell>,
                            seller: <Cell><Txt>{l.seller_name}</Txt></Cell>,
                            status: (
                                <Cell className="flex-wrap">
                                    <Tag status={l.status} testid={`mp-status-${l.slug}`} />
                                    {l.featured && <Tag cls="border-[#1a1a1a] bg-[#1a1a1a] text-white bo-inv"><Flame size={9} /> featured</Tag>}
                                </Cell>
                            ),
                            actions: (
                                <Cell className="justify-end">
                                    {l.status !== "active" && <Btn tone="solid" onClick={() => act(l.id, "approve")} testid={`mp-approve-${l.slug}`}><Check size={10} /> Approve</Btn>}
                                    {l.status === "pending" && <Btn onClick={() => act(l.id, "reject")} testid={`mp-reject-${l.slug}`}><X size={10} /> Reject</Btn>}
                                    <Btn onClick={() => feature(l)} testid={`mp-feature-${l.slug}`}><Flame size={10} /> {l.featured ? "Unfeature" : "Feature 7d"}</Btn>
                                    <IconBtn onClick={() => act(l.id, "remove")} testid={`mp-remove-${l.slug}`} title="Remove this ad" danger><Trash2 size={12} /></IconBtn>
                                </Cell>
                            ),
                        })} />
                </Section>

                <Section title={`${inquiries.length} buyer messages`} pad={false} testid="mp-inquiries" note="what buyers wrote to sellers · newest first">
                    <Sheet cols={MSG_COLS} rows={inquiries.slice(0, 25)} rowKey={(q) => q.id} testid="mp-inquiries-sheet" minWidth={800}
                        empty="No messages yet."
                        cells={(q) => ({
                            when: <Cell><Txt sub>{when(q.created_at)}</Txt></Cell>,
                            who: <Cell><Two a={q.name} b={[q.email, q.phone].filter(Boolean).join(" · ")} /></Cell>,
                            ad: <Cell><Txt>{q.listing_title}</Txt></Cell>,
                            message: <Cell><Txt sub title={q.message}>{q.message}</Txt></Cell>,
                        })} />
                </Section>

                <ServiceRequests />
            </div>
        </div>
    );
}
